import React, { useEffect, useState } from 'react';
import { Alert, Button, DatePicker, Empty, Form, Input, Modal, Select, Space, Spin, Table, Tag } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl_SPB } from '../../../BaseUrl';
import { formatDisplayDate, isExpiredDate, parseFlexibleDate } from './expiryUtils';

const STATUS_OPTIONS = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Expiring Soon', value: 'EXPIRING_SOON' },
  { label: 'Expired', value: 'EXPIRED' },
  { label: 'Suspended', value: 'SUSPENDED' },
];

const LicenceMembershipPanel = ({ employeeId, token, onDataChange }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRecords();
  }, [employeeId]);

  const fetchRecords = async () => {
    if (!token || !employeeId) return;
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${baseUrl_SPB}/api/v1/onboarding/licence/staff/${employeeId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load licence records');
      }

      const data = await response.json();
      const nextRecords = Array.isArray(data) ? data : [];
      setRecords(nextRecords);
      onDataChange?.(nextRecords);
    } catch (err) {
      console.error(err);
      setError('Failed to load licences and memberships.');
      setRecords([]);
      onDataChange?.([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ status: 'ACTIVE' });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      type: record.type || '',
      memOrLicenceNumber: record.memOrLicenceNumber || '',
      details: record.details || '',
      issueDate: parseFlexibleDate(record.issueDate),
      expiryDate: parseFlexibleDate(record.expiryDate),
      status: record.status || 'ACTIVE',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setError('');

      const payload = {
        ...(editingRecord || {}),
        staffId: employeeId,
        type: values.type,
        memOrLicenceNumber: values.memOrLicenceNumber,
        details: values.details || '',
        issueDate: values.issueDate ? values.issueDate.format('YYYY-MM-DD') : '',
        expiryDate: values.expiryDate ? values.expiryDate.format('YYYY-MM-DD') : '',
        status: values.status,
      };

      await axios.post(`${baseUrl_SPB}/api/v1/onboarding/licence/staff/${employeeId}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setModalOpen(false);
      form.resetFields();
      setEditingRecord(null);
      await fetchRecords();
    } catch (err) {
      if (err?.errorFields) return;
      console.error(err);
      setError('Failed to save licence or membership record.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Delete record',
      content: `Delete ${record.type || 'this record'}?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl_SPB}/api/v1/onboarding/licence/staff/${employeeId}/${record.id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          await fetchRecords();
        } catch (err) {
          console.error(err);
          setError('Failed to delete licence or membership record.');
        }
      },
    });
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (value) => value || '—',
    },
    {
      title: 'Number',
      dataIndex: 'memOrLicenceNumber',
      key: 'memOrLicenceNumber',
      render: (value) => value || '—',
    },
    {
      title: 'Issue Date',
      dataIndex: 'issueDate',
      key: 'issueDate',
      render: (value) => formatDisplayDate(value),
    },
    {
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      render: (value) => (
        <Space size={6}>
          <span>{formatDisplayDate(value)}</span>
          {isExpiredDate(value) ? <Tag color="red">Expired</Tag> : null}
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => {
        const color = value === 'EXPIRED' ? 'red' : value === 'EXPIRING_SOON' ? 'orange' : value === 'ACTIVE' ? 'green' : 'default';
        return <Tag color={color}>{value || '—'}</Tag>;
      },
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
      render: (value) => value || '—',
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      render: (_, record) => (
        <Space size={4}>
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)} />
          <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      {error ? <Alert type="error" showIcon message={error} closable onClose={() => setError('')} /> : null}

      <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
        Add licence or membership
      </Button>

      <Spin spinning={loading}>
        {records.length === 0 ? (
          <Empty description="No licences or memberships" />
        ) : (
          <Table
            rowKey={(record) => record.id || `${record.type}-${record.memOrLicenceNumber}`}
            dataSource={records}
            columns={columns}
            pagination={false}
            size="small"
            scroll={{ x: 900 }}
          />
        )}
      </Spin>

      <Modal
        open={modalOpen}
        title={editingRecord ? 'Edit Licence / Membership' : 'Add Licence / Membership'}
        okText={editingRecord ? 'Update' : 'Save'}
        cancelText="Cancel"
        onOk={handleSave}
        onCancel={() => {
          setModalOpen(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Type is required' }]}>
            <Input placeholder="e.g. Electrician, WWCC, Forklift, Membership" />
          </Form.Item>
          <Form.Item name="memOrLicenceNumber" label="Membership / Licence Number" rules={[{ required: true, message: 'Number is required' }]}>
            <Input placeholder="Enter reference number" />
          </Form.Item>
          <Form.Item name="details" label="Details">
            <Input.TextArea rows={3} placeholder="Additional notes or scope" />
          </Form.Item>
          <Form.Item name="issueDate" label="Issue Date">
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="expiryDate" label="Expiry Date" rules={[{ required: true, message: 'Expiry date is required' }]}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Status is required' }]}>
            <Select options={STATUS_OPTIONS} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default LicenceMembershipPanel;
